import React, { useCallback, useMemo } from 'react';
import { Task as TaskModel } from '../../models/task';
import { AppCard } from '../app/appCard';

interface TaskProp {
  openUsersDialog(): void;
  task: TaskModel;
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit(task: TaskModel): void;
  openEditDialog(): void;
  onDuplicate(task: TaskModel): void;
  onDelete(task: TaskModel): void;
}

export function Task({
  openUsersDialog,
  onEdit,
  task,
  canEdit = true,
  canDelete = true,
  openEditDialog,
  onDuplicate,
  onDelete,
}: TaskProp): JSX.Element {
  const handleDelete = useCallback(() => {
    onDelete(task);
  }, [onDelete, task]);

  const handleUsers = useCallback(() => {
    openUsersDialog();
    onEdit(task);
  }, [onEdit, openUsersDialog, task]);

  const handleEdit = useCallback(() => {
    openEditDialog();
    onEdit(task);
  }, [onEdit, openEditDialog, task]);

  const handleDuplicate = useCallback(() => {
    onDuplicate(task);
  }, [onDuplicate, task]);

  const taskEmployeesNames = useMemo(() => {
    if (task.employees.length === 0) return '0';

    const activeEmployees = task.employees.filter(
      (employees) => employees.isActiveOnTask
    );

    const employees = activeEmployees.map((employee) => {
      const [lastName, firstName] = employee.name.split(',');

      return `${lastName.toUpperCase()}${firstName}`;
    });

    const userToDisplay = 6;

    if (employees.length > 7) {
      return `${employees.slice(0, userToDisplay).join(', ')} and ${
        employees.length - userToDisplay
      } more `;
    }

    return employees.join(', ');
  }, [task]);

  const cardActionButtons = useMemo(
    () =>
      [
        {
          label: 'Delete',
          onClick: handleDelete,
          isPrimary: false,
          isVisible: canDelete,
        },
        {
          label: 'Users',
          onClick: handleUsers,
          isPrimary: false,
          isVisible: true,
        },
        {
          label: 'Edit',
          onClick: handleEdit,
          isPrimary: true,
          isVisible: canEdit,
        },
        {
          label: 'Duplicate',
          onClick: handleDuplicate,
          isPrimary: true,
          isVisible: true,
        },
      ]
        .filter((button) => button.isVisible)
        .map(({ isVisible, ...button }) => button),
    [canDelete, canEdit, handleDelete, handleDuplicate, handleEdit, handleUsers]
  );

  return (
    <AppCard
      title={`${task.name}`}
      details={[`Status: ${task.status}`, `Users: ${taskEmployeesNames}`]}
      buttons={cardActionButtons}
    />
  );
}
